<template>
    <!-- Checkride Details Modal -->
    <div class="modal" :class="{ 'is-active': isOpen }" id="checkride-modal">
        <div class="modal-background" @click="$emit('close')"></div>
        <div class="modal-card" style="width: 90%; max-width: 1200px;">
            <header class="modal-card-head">
                <p class="modal-card-title">
                    <span class="icon">
                        <font-awesome-icon :icon="['fas', 'file-alt']" />
                    </span>
                    Checkride Details - {{ checkride?.type === 1 ? 'Part 135.299' : 'Part 135.293' }}
                </p>
                <button class="delete" @click="$emit('close')"></button>
            </header>
            <div class="modal-card-body" id="printable-content">
                <div v-if="checkride">
                    <!-- Header Information -->
                    <div class="box">
                        <div class="has-text-centered mb-4">
                            <img v-once class="logo" src="@/assets/logo.png" style="height: 32px; vertical-align: middle; margin-right: 10px;">
                            <span class="title is-4" style="vertical-align: middle;">STAJets Checkride Records Check</span>
                        </div>
                        <div class="columns">
                            <div class="column">
                                <h4 class="title is-5">Flight Information</h4>
                                <p><strong>Date:</strong> {{ formatDate(checkride.scheduledDatetime) }} {{ formatTime(checkride.scheduledDatetime) }}</p>
                                <p><strong>Routing:</strong> {{ checkride.routing }}</p>
                                <p><strong>Tail #:</strong> {{ checkride.tailNumber || '—' }}</p>
                                <p><strong>Type:</strong> {{ checkride.type === 1 ? 'Part 135.299 Checkride' : 'Part 135.293 Checkride' }}</p>
                            </div>
                            <div class="column">
                                <h4 class="title is-5">Crew Information</h4>
                                <p><strong>{{ checkride.type === 2 ? 'Pilot' : 'PIC' }}:</strong> {{ checkride.picName }}</p>
                                <p v-if="checkride.type !== 2"><strong>SIC:</strong> {{ checkride.sicName }}</p>
                                <p><strong>Check Airman:</strong> {{ checkride.checkAirmanName }}</p>
                            </div>
                        </div>
                    </div>

                    <!-- Validation Results -->
                    <div class="columns" v-if="checkride">
                        <!-- PIC Validation -->
                        <div class="column">
                            <div class="box">
                                <h4 class="title is-5">
                                    <span class="icon" :class="checkride.picCheckResult ? 'has-text-success' : 'has-text-danger'">
                                        <font-awesome-icon :icon="checkride.picCheckResult ? ['fas', 'check-circle'] : ['fas', 'times-circle']" />
                                    </span>
                                    {{ checkride.type === 2 ? 'Pilot' : 'PIC' }} Validation
                                </h4>
                                <div class="content">
                                    <div v-if="parsedPicResults" class="validation-results">
                                        <div v-for="(result, docName) in parsedPicResults" :key="docName" class="validation-item">
                                            <span class="doc-name">{{ docName }}:</span>
                                            <span class="result" :class="getResultClass(result)">{{ result }}</span>
                                        </div>
                                    </div>
                                    <pre v-else class="has-text-left" style="white-space: pre-wrap; font-family: inherit; font-size: 0.875rem;">{{ checkride.picCheckMessage || 'No validation data available' }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="columns" v-if="checkride && checkride.type !== 2">
                        <!-- SIC Validation - Only show for 299 checkrides -->
                        <div class="column">
                            <div class="box">
                                <h4 class="title is-5">
                                    <span class="icon" :class="checkride.sicCheckResult ? 'has-text-success' : 'has-text-danger'">
                                        <font-awesome-icon :icon="checkride.sicCheckResult ? ['fas', 'check-circle'] : ['fas', 'times-circle']" />
                                    </span>
                                    SIC Validation
                                </h4>
                                <div class="content">
                                    <div v-if="parsedSicResults" class="validation-results">
                                        <div v-for="(result, docName) in parsedSicResults" :key="docName" class="validation-item">
                                            <span class="doc-name">{{ docName }}:</span>
                                            <span class="result" :class="getResultClass(result)">{{ result }}</span>
                                        </div>
                                    </div>
                                    <pre v-else class="has-text-left" style="white-space: pre-wrap; font-family: inherit; font-size: 0.875rem;">{{ checkride.sicCheckMessage || 'No validation data available' }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="columns" v-if="checkride">
                        <!-- Check Airman Validation -->
                        <div class="column">
                            <div class="box">
                                <h4 class="title is-5">
                                    <span class="icon" :class="checkride.checkAirmanCheckResult ? 'has-text-success' : 'has-text-danger'">
                                        <font-awesome-icon :icon="checkride.checkAirmanCheckResult ? ['fas', 'check-circle'] : ['fas', 'times-circle']" />
                                    </span>
                                    Check Airman Validation
                                </h4>
                                <div class="content">
                                    <div v-if="parsedCheckAirmanResults" class="validation-results">
                                        <div v-for="(result, docName) in parsedCheckAirmanResults" :key="docName" class="validation-item">
                                            <span class="doc-name">{{ docName }}:</span>
                                            <span class="result" :class="getResultClass(result)">{{ result }}</span>
                                        </div>
                                    </div>
                                    <pre v-else class="has-text-left" style="white-space: pre-wrap; font-family: inherit; font-size: 0.875rem;">{{ checkride.checkAirmanCheckMessage || 'No validation data available' }}</pre>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="notification is-info is-light mt-4">
                <p class="has-text-centered">
                    <strong>CERTIFICATION:</strong> I hereby certify that the above pilot training and checking records are accurate and complete, 
                    and that all training and checking will be conducted in accordance with FAR Part 135.293, 135.297, 135.299, and 135.331 
                    as applicable. All documents and currency requirements have been verified as current and valid.
                </p>
            </div>
            <footer class="modal-card-foot">
                <div class="buttons">
                    <button v-if="allChecksPass" class="button is-info" @click="emailCheckride" 
                            :disabled="isEmailSending" :class="{ 'is-loading': isEmailSending }">
                        <span class="icon">
                            <font-awesome-icon :icon="['fas', 'envelope']" />
                        </span>
                        <span>{{ isEmailSending ? 'Sending...' : 'Email' }}</span>
                    </button>
                    <button v-if="allChecksPass" class="button is-primary" @click="printCheckride">
                        <span class="icon">
                            <font-awesome-icon :icon="['fas', 'print']" />
                        </span>
                        <span>Print</span>
                    </button>
                    <div v-if="!allChecksPass" class="notification is-warning is-light">
                        <p><strong>Notice:</strong> Email and print options are disabled because one or more validation checks have failed. Please resolve all validation issues before proceeding.</p>
                    </div>
                    <button class="button" @click="$emit('close')">Close</button>
                </div>
            </footer>
        </div>
    </div>
</template>

<script>
import { ref, computed } from 'vue';
import { useCheckrides } from '@/lib/checkrides';
import { useSettings } from '@/lib/settings';

export default {
    name: 'CheckrideModal',
    props: {
        isOpen: {
            type: Boolean,
            default: false
        },
        checkride: {
            type: Object,
            default: null
        }
    },
    emits: ['close'],
    setup(props) {
        const isEmailSending = ref(false);
        const checkrides = useCheckrides();

        // Computed property to check if all validation checks pass for the selected checkride
        const allChecksPass = computed(() => {
            if (!props.checkride) return false;
            return props.checkride.picCheckResult && 
                   props.checkride.sicCheckResult && 
                   props.checkride.checkAirmanCheckResult;
        });

        // Parse JSON validation results
        const parsedPicResults = computed(() => {
            if (!props.checkride?.picCheckMessage) return null;
            try {
                return JSON.parse(props.checkride.picCheckMessage);
            } catch (e) {
                return null;
            }
        });

        const parsedSicResults = computed(() => {
            if (!props.checkride?.sicCheckMessage) return null;
            try {
                return JSON.parse(props.checkride.sicCheckMessage);
            } catch (e) {
                return null;
            }
        });

        const parsedCheckAirmanResults = computed(() => {
            if (!props.checkride?.checkAirmanCheckMessage) return null;
            try {
                return JSON.parse(props.checkride.checkAirmanCheckMessage);
            } catch (e) {
                return null;
            }
        });

        // Get CSS class for validation result
        const getResultClass = (result) => {
            if (result === 'Valid') {
                return 'has-text-success';
            } else if (result && result.includes('does not match')) {
                return 'has-text-danger';
            } else if (result && (result.includes('MISSING') || result.includes('EXPIRED'))) {
                return 'has-text-danger';
            }
            return 'has-text-warning';
        };

        const formatDate = (timestamp) => {
            if (!timestamp) return '';
            const options = { year: 'numeric', month: 'short', day: 'numeric' };
            return new Date(timestamp).toLocaleDateString(undefined, options);
        };

        const formatTime = (timestamp) => {
            if (!timestamp) return '';
            const options = { hour: '2-digit', minute: '2-digit' };
            return new Date(timestamp).toLocaleTimeString(undefined, options);
        };

        const emailCheckride = async () => {
            if (!props.checkride || isEmailSending.value) return;
            
            try {
                // Get email recipients from settings
                const { getSetting, fetchSettings } = useSettings();
                
                // Ensure settings are loaded
                if (!getSetting('checkrideEmails')) {
                    await fetchSettings?.();
                }
                
                const emails = getSetting('checkrideEmails');
                const emailList = emails ? emails.split(';').map(e => e.trim()).filter(Boolean).join('\n') : 'No email addresses configured';
                
                const confirmed = confirm(`Are you sure you want to email this checkride certificate to:\n\n${emailList}`);
                if (!confirmed) return;
                
                isEmailSending.value = true;
                
                const result = await checkrides.emailCheckride(props.checkride.id);
                
                if (result.success) {
                    alert("Checkride certificate emailed successfully!");
                } else {
                    alert(`Failed to email checkride: ${result.message || 'Unknown error'}`);
                }
            } catch (error) {
                console.error('Error emailing checkride:', error);
                alert('Failed to email checkride. Please try again.');
            } finally {
                isEmailSending.value = false;
            }
        };

        const printCheckride = () => {
            // Hide modal temporarily for printing
            const modal = document.getElementById('checkride-modal');
            const originalDisplay = modal.style.display;
            modal.style.display = 'none';
            
            // Create a new window for printing
            const printWindow = window.open('', '_blank');
            const printContent = document.getElementById('printable-content').innerHTML;

            printWindow.document.write(`
                <!DOCTYPE html>
                <html>
                <head>
                    <title>Checkride Details - ${props.checkride?.type === 1 ? 'Part 135.299' : 'Part 135.293'}</title>
                    <style>
                        body { font-family: Arial, sans-serif; margin: 20px; font-size: 12px; }
                        .box { border: 1px solid #ddd; padding: 15px; margin: 10px 0; page-break-inside: avoid; }
                        .columns { display: flex; gap: 20px; page-break-inside: avoid; }
                        .column { flex: 1; }
                        .title { font-size: 1.2em; font-weight: bold; margin-bottom: 10px; }
                        .notification { background-color: #f0f8ff; border: 1px solid #ccc; padding: 15px; margin: 10px 0; page-break-inside: avoid; }
                        .has-text-centered { text-align: center; }
                        .has-text-success { color: green; }
                        .has-text-danger { color: red; }
                        .has-text-warning { color: orange; }
                        .icon { display: inline-block; width: 16px; text-align: center; margin-right: 8px; }
                        pre { white-space: pre-wrap; font-family: inherit; font-size: 0.875rem; margin: 0; }
                        .validation-results { font-size: 0.875rem; }
                        .validation-item { margin: 5px 0; line-height: 1.4; }
                        .doc-name { font-weight: bold; margin-right: 8px; }
                        .result { }
                        .signature-line { 
                            border-bottom: 2px solid #000; 
                            width: 300px; 
                            height: 30px; 
                            margin: 20px 0 5px 0; 
                            display: inline-block;
                        }
                        .help { font-size: 0.8em; color: #666; margin-top: 5px; }
                        .signature-section { margin-top: 30px; }
                        .signature-column { width: 45%; display: inline-block; vertical-align: top; margin-right: 5%; }
                        
                        /* Hide input fields and show signature lines instead */
                        input[type="text"] { display: none; }
                        .signature-placeholder { 
                            border-bottom: 2px solid #000; 
                            width: 100%; 
                            height: 30px; 
                            margin: 10px 0;
                        }
                        
                        @media print {
                            body { margin: 0; font-size: 11px; }
                            .box { break-inside: avoid; margin: 5px 0; }
                            .columns { break-inside: avoid; }
                            .signature-section { page-break-inside: avoid; }
                        }
                    </style>
                </head>
                <body>
                    ${printContent.replace(/<input[^>]*>/g, '<div class="signature-placeholder"></div>')}
                </body>
                </html>
            `);
            
            printWindow.document.close();
            
            // Wait for content to load then print
            printWindow.onload = () => {
                printWindow.print();
                printWindow.close();
            };
            
            // Restore modal display
            modal.style.display = originalDisplay;
        };

        return {
            isEmailSending,
            allChecksPass,
            parsedPicResults,
            parsedSicResults,
            parsedCheckAirmanResults,
            getResultClass,
            formatDate,
            formatTime,
            emailCheckride,
            printCheckride
        };
    }
};
</script>

<style scoped>
.validation-results {
    font-size: 0.875rem;
}

.validation-item {
    margin: 5px 0;
    line-height: 1.4;
    padding: 2px 0;
}

.doc-name {
    font-weight: bold;
    margin-right: 8px;
    min-width: 200px;
    display: inline-block;
}

.result {
    word-wrap: break-word;
}

.has-text-success {
    color: #48c774;
}

.has-text-danger {
    color: #f14668;
}

.has-text-warning {
    color: #ffdd57;
}
</style>